import { storeWrapper } from '../storeWrapper'

const initialState = {
  clientsTableFilterCount: 0,
  clientsTableFilters: {
    clientMatterNo: null,
    filters: {
      address: null,
      clientName: null,
      clientNo: null,
      clientType: null,
      emailAddress: null,
      highRisk: null
    },
    orderByColumns: [
      {
        columnName: null,
        direction: 0
      }
    ],
    pageSize: null,
    startAtIndex: null,
    totalRowCount: null
  },
  expensesTableFilterCount: 0,
  expensesTableFilters: {
    filters: {
      dateIncurred: {
        date: null,
        dateComparisonOperator: null
      },
      claimRecipient: null,
      claimType: null,
      claimDescription: null,
      associatedMatter: null,
      receipt: null,
      recoverable: null,
      claimAmount: {
        value: null,
        numberComparisonOperator: null
      },
      status: null,
      comments: null
    },
    pageSize: null,
    startAtIndex: null,
    totalRowCount: null
  },
  mattersTableFilterCount: 0,
  mattersTableFilters: {
    clientMatterNo: null,
    filters: {
      archiveLocation: null,
      archiveReference: null,
      branchId: null,
      clientBalance: {
        value: null,
        numberComparisonOperator: 0
      },
      clientName: null,
      clientNo: null,
      clientType: null,
      dateArchived: {
        date: null,
        dateComparisonOperator: 0
      },
      dateCompleted: {
        date: null,
        dateComparisonOperator: 0
      },
      dateOpened: {
        date: null,
        dateComparisonOperator: 0
      },
      departmentId: null,
      disbursementsBalance: {
        value: null,
        numberComparisonOperator: 0
      },
      feeEarnerIds: null,
      highRisk: null,
      isArchived: null,
      isClientPortalEnabled: null,
      isCompleted: null,
      isProspect: null,
      isUserRestricted: null,
      lastMatterDate: {
        date: null,
        dateComparisonOperator: 0
      },
      matterDescription: null,
      matterNo: null,
      matterWipBalance: {
        value: null,
        numberComparisonOperator: 0
      },
      nextKeyDate: {
        date: null,
        dateComparisonOperator: 0
      },
      officeBalance: {
        value: null,
        numberComparisonOperator: 0
      },
      privateOrLegalAid: null,
      prospectLiveDate: {
        date: null,
        dateComparisonOperator: 0
      },
      reviewDate: {
        date: null,
        dateComparisonOperator: 0
      },
      supervisingFeeEarnerIds: null,
      workflowStatus: null,
      workTypeIds: null
    },
    orderByColumns: null,
    pageSize: null,
    startAtIndex: null,
    totalRowCount: null
  },
  keyDatesTableFilterCount: 0,
  keyDatesTableFilters: {
    totalRowCount: null,
    filters: {
      clientName: null,
      clientNo: null,
      criminalNotes: null,
      keyDate: { date: null, dateComparisonOperator: 1 },
      dateMet: null,
      feeEarnerIds: null,
      isHoliday: null,
      isWeekend: null,
      keyDateLocationIds: null,
      keyDateTypeId: null,
      matterNo: null,
      mustAttend: null,
      notes: null,
      orderByColumns: null,
      pageSize: null,
      startAtIndex: null
    }
  },
  matterSpecificKeyDatesTableFilterCount: 0,
  matterSpecificKeyDatesTableFilters: {
    totalRowCount: null,
    filters: {
      clientName: null,
      clientNo: null,
      criminalNotes: null,
      keyDate: { date: null, dateComparisonOperator: 1 },
      dateMet: null,
      feeEarnerIds: null,
      isHoliday: null,
      isWeekend: null,
      keyDateLocationId: null,
      keyDateTypeIds: null,
      matterNo: null,
      mustAttend: null,
      notes: null,
      orderByColumns: null,
      pageSize: null,
      startAtIndex: null
    }
  },
  overtimeTableFilterCount: 0,
  overtimeTableFilters: {
    filters: {
      dateIncurred: {
        date: null,
        dateComparisonOperator: null
      },
      claimRecipient: null,
      claimType: null,
      claimDescription: null,
      associatedMatter: null,
      claimAmount: {
        value: null,
        numberComparisonOperator: null
      },
      overtimeHours: {
        value: null,
        numberComparisonOperator: null
      },
      status: null,
      comments: null
    },
    pageSize: null,
    startAtIndex: null,
    totalRowCount: null
  },
  thirdPartyFormsTableFilterCount: 0,
  thirdPartyFormsTableFilters: {
    filters: {
      formName: null,
      formDescription: null,
      updatedDate: {
        date: null,
        dateComparisonOperator: null
      },
      createdBy: null,
      status: null
    },
    pageSize: null,
    startAtIndex: null,
    totalRowCount: null
  },
  documentBundlesTableFilterCount: 0,
  documentBundlesTableFilters: {
    totalRowCount: null,
    filters: {
      clientNo: null,
      matterNo: null,
      name: null,
      description: null,
      createdDate: { date: null, dateComparisonOperator: 0 },
      createdBy: null,
      pageSize: null,
      startAtIndex: null
    }
  }
}

// Keys inside key-dates "filters" that are transport-only and must NOT be counted as active user filters
const KEYDATES_IGNORED_FOR_COUNT = new Set(['pageSize', 'startAtIndex', 'orderByColumns'])

export const useDataGridFilterStore = storeWrapper('useDataGridFilterStore', (set, get) => ({
  ...initialState,

  setClientsTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { clientsTableFilters } = get()
    const updatedValues = {
      clientsTableFilters: {
        ...clientsTableFilters,
        filters: {
          ...clientsTableFilters.filters,
          ...filters
        }
      }
    }

    set(updatedValues)

    // count how many filters are different to the default
    const defaultFilters = initialState.clientsTableFilters.filters
    const clientsTableFilterCount = countAppliedFilters(updatedValues.clientsTableFilters.filters, defaultFilters)

    set({ clientsTableFilterCount })
  },

  setClientsTableFilters: ({
    clientMatterNo,
    pageSize,
    orderByColumns,
    startAtIndex,
    totalRowCount,
    filterType,
    value
  }) => {
    const { clientsTableFilters } = get()

    clientMatterNo && (clientsTableFilters[clientMatterNo] = value)
    filterType && (clientsTableFilters.filters[filterType] = value)
    pageSize && (clientsTableFilters[pageSize] = value)
    orderByColumns && (clientsTableFilters[orderByColumns] = value)
    startAtIndex && (clientsTableFilters[startAtIndex] = value)
    totalRowCount && (clientsTableFilters[totalRowCount] = value)

    set({
      clientsTableFilters: {
        ...clientsTableFilters,
        filters: {
          ...clientsTableFilters.filters
        }
      }
    })

    // count how many filters are different to the default
    const defaultFilters = initialState.clientsTableFilters.filters
    const clientsTableFilterCount = countAppliedFilters(clientsTableFilters.filters, defaultFilters)

    set({ clientsTableFilterCount })
  },

  setExpensesTableFilters: ({
    dateIncurred,
    claimRecipient,
    claimType,
    claimDescription,
    associatedMatter,
    receipt,
    recoverable,
    claimAmount,
    status,
    comments,
    pageSize,
    startAtIndex
  }) => {
    const { expensesTableFilters } = get()

    const updatedFilters = { ...expensesTableFilters.filters }
    if (dateIncurred !== undefined) updatedFilters.dateIncurred = dateIncurred
    if (claimRecipient !== undefined) updatedFilters.claimRecipient = claimRecipient
    if (claimType !== undefined) updatedFilters.claimType = claimType
    if (claimDescription !== undefined) updatedFilters.claimDescription = claimDescription
    if (associatedMatter !== undefined) updatedFilters.associatedMatter = associatedMatter
    if (receipt !== undefined) updatedFilters.receipt = receipt
    if (recoverable !== undefined) updatedFilters.recoverable = recoverable
    if (claimAmount !== undefined) updatedFilters.claimAmount = claimAmount
    if (status !== undefined) updatedFilters.status = status
    if (comments !== undefined) updatedFilters.comments = comments

    set({
      expensesTableFilters: {
        ...expensesTableFilters,
        filters: updatedFilters,
        pageSize: pageSize !== undefined ? pageSize : expensesTableFilters.pageSize,
        startAtIndex: startAtIndex !== undefined ? startAtIndex : expensesTableFilters.startAtIndex
      }
    })

    // count how many filters are different to the default
    const defaultFilters = initialState.expensesTableFilters.filters
    const expensesTableFilterCount = countAppliedFilters(updatedFilters, defaultFilters)

    set({ expensesTableFilterCount })
  },

  setExpensesTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { expensesTableFilters } = get()
    const updatedValues = {
      expensesTableFilters: {
        ...expensesTableFilters,
        filters: {
          ...expensesTableFilters.filters,
          ...filters
        }
      }
    }

    set(updatedValues)

    // count how many filters are different to the default
    const defaultFilters = initialState.expensesTableFilters.filters
    const expensesTableFilterCount = countAppliedFilters(updatedValues.expensesTableFilters.filters, defaultFilters)

    set({ expensesTableFilterCount })
  },

  setOvertimeTableFilters: ({
    dateIncurred,
    claimRecipient,
    claimType,
    claimDescription,
    associatedMatter,
    claimAmount,
    overtimeHours,
    status,
    comments,
    pageSize,
    startAtIndex
  }) => {
    const { overtimeTableFilters } = get()

    const updatedFilters = { ...overtimeTableFilters.filters }
    if (dateIncurred !== undefined) updatedFilters.dateIncurred = dateIncurred
    if (claimRecipient !== undefined) updatedFilters.claimRecipient = claimRecipient
    if (claimType !== undefined) updatedFilters.claimType = claimType
    if (claimDescription !== undefined) updatedFilters.claimDescription = claimDescription
    if (associatedMatter !== undefined) updatedFilters.associatedMatter = associatedMatter
    if (claimAmount !== undefined) updatedFilters.claimAmount = claimAmount
    if (overtimeHours !== undefined) updatedFilters.overtimeHours = overtimeHours
    if (status !== undefined) updatedFilters.status = status
    if (comments !== undefined) updatedFilters.comments = comments

    set({
      overtimeTableFilters: {
        ...overtimeTableFilters,
        filters: updatedFilters,
        pageSize: pageSize !== undefined ? pageSize : overtimeTableFilters.pageSize,
        startAtIndex: startAtIndex !== undefined ? startAtIndex : overtimeTableFilters.startAtIndex
      }
    })

    // count how many filters are different to the default
    const defaultFilters = initialState.overtimeTableFilters.filters
    const overtimeTableFilterCount = countAppliedFilters(updatedFilters, defaultFilters)

    set({ overtimeTableFilterCount })
  },

  setOvertimeTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { overtimeTableFilters } = get()
    const updatedValues = {
      overtimeTableFilters: {
        ...overtimeTableFilters,
        filters: {
          ...overtimeTableFilters.filters,
          ...filters
        }
      }
    }

    set(updatedValues)

    // count how many filters are different to the default
    const defaultFilters = initialState.overtimeTableFilters.filters
    const overtimeTableFilterCount = countAppliedFilters(updatedValues.overtimeTableFilters.filters, defaultFilters)

    set({ overtimeTableFilterCount })
  },

  setThirdPartyFormsTableFilters: ({
    formName,
    formDescription,
    updatedDate,
    createdBy,
    status,
    pageSize,
    startAtIndex
  }) => {
    const { thirdPartyFormsTableFilters } = get()

    const updatedFilters = { ...thirdPartyFormsTableFilters.filters }
    if (formName !== undefined) updatedFilters.formName = formName
    if (formDescription !== undefined) updatedFilters.formDescription = formDescription
    if (updatedDate !== undefined) updatedFilters.updatedDate = updatedDate
    if (createdBy !== undefined) updatedFilters.createdBy = createdBy
    if (status !== undefined) updatedFilters.status = status

    set({
      thirdPartyFormsTableFilters: {
        ...thirdPartyFormsTableFilters,
        filters: updatedFilters,
        pageSize: pageSize !== undefined ? pageSize : thirdPartyFormsTableFilters.pageSize,
        startAtIndex: startAtIndex !== undefined ? startAtIndex : thirdPartyFormsTableFilters.startAtIndex
      }
    })

    const defaultFilters = initialState.thirdPartyFormsTableFilters.filters
    const thirdPartyFormsTableFilterCount = countAppliedFilters(updatedFilters, defaultFilters)

    set({ thirdPartyFormsTableFilterCount })
  },

  setThirdPartyFormsTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { thirdPartyFormsTableFilters } = get()
    const updatedValues = {
      thirdPartyFormsTableFilters: {
        ...thirdPartyFormsTableFilters,
        filters: {
          ...thirdPartyFormsTableFilters.filters,
          ...filters
        }
      }
    }

    set(updatedValues)

    const defaultFilters = initialState.thirdPartyFormsTableFilters.filters
    const thirdPartyFormsTableFilterCount = countAppliedFilters(
      updatedValues.thirdPartyFormsTableFilters.filters,
      defaultFilters
    )

    set({ thirdPartyFormsTableFilterCount })
  },

  setMattersTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { mattersTableFilters } = get()
    const updatedValues = {
      mattersTableFilters: {
        ...mattersTableFilters,
        filters: {
          ...mattersTableFilters.filters,
          ...filters
        }
      }
    }

    set(updatedValues)

    // count how many filters are different to the default
    const defaultFilters = initialState.mattersTableFilters.filters
    const mattersTableFilterCount = countAppliedFilters(updatedValues.mattersTableFilters.filters, defaultFilters)

    set({ mattersTableFilterCount })
  },

  setMattersTableFilters: ({
    clientMatterNo,
    pageSize,
    orderByColumns,
    startAtIndex,
    totalRowCount,
    filterType,
    value
  }) => {
    const { mattersTableFilters } = get()

    clientMatterNo && (mattersTableFilters[clientMatterNo] = value)
    filterType && (mattersTableFilters.filters[filterType] = value)
    pageSize && (mattersTableFilters[pageSize] = value)
    orderByColumns && (mattersTableFilters[orderByColumns] = value)
    startAtIndex && (mattersTableFilters[startAtIndex] = value)
    totalRowCount && (mattersTableFilters[totalRowCount] = value)

    set({
      mattersTableFilters: {
        ...mattersTableFilters,
        filters: {
          ...mattersTableFilters.filters
        }
      }
    })

    // count how many filters are different to the default
    const defaultFilters = initialState.mattersTableFilters.filters
    const mattersTableFilterCount = countAppliedFilters(mattersTableFilters.filters, defaultFilters)

    set({ mattersTableFilterCount })
  },

  setKeyDatesTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return
    const { keyDatesTableFilters } = get()
    const next = {
      ...keyDatesTableFilters,
      filters: { ...keyDatesTableFilters.filters, ...filters }
    }
    set({ keyDatesTableFilters: next })
    const defaultFilters = initialState.keyDatesTableFilters.filters
    const keyDatesTableFilterCount = countAppliedFilters(next.filters, defaultFilters, KEYDATES_IGNORED_FOR_COUNT)
    set({ keyDatesTableFilterCount })
  },

  setMatterSpecificKeyDatesTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return

    const { matterSpecificKeyDatesTableFilters } = get()
    const updatedValues = {
      matterSpecificKeyDatesTableFilters: {
        ...matterSpecificKeyDatesTableFilters,
        filters: {
          ...matterSpecificKeyDatesTableFilters.filters,
          ...filters
        }
      }
    }

    set({ matterSpecificKeyDatesTableFilters: updatedValues.matterSpecificKeyDatesTableFilters })

    // count how many filters are different to the default (ignore transport keys)
    const defaultFilters = initialState.matterSpecificKeyDatesTableFilters.filters

    const matterSpecificKeyDatesTableFilterCount = countAppliedFilters(
      updatedValues.matterSpecificKeyDatesTableFilters.filters,
      defaultFilters,
      KEYDATES_IGNORED_FOR_COUNT
    )

    set({ matterSpecificKeyDatesTableFilterCount })
  },

  setKeyDatesTableFilters: ({
    clientMatterNo,
    pageSize,
    orderByColumns,
    startAtIndex,
    totalRowCount,
    filterType,
    value,
    filters,
    isMatterSpecific
  }) => {
    const { keyDatesTableFilters, matterSpecificKeyDatesTableFilters } = get()

    const current = isMatterSpecific ? matterSpecificKeyDatesTableFilters : keyDatesTableFilters

    let next = {
      ...current,
      filters: { ...current.filters }
    }

    if (filters && typeof filters === 'object') {
      next.filters = { ...next.filters, ...filters }
    }

    if (filterType != null) {
      const key = String(filterType).trim()
      if (key) {
        const newVal = value === undefined ? null : value
        next.filters[key] = newVal
      }
    }

    if (clientMatterNo !== undefined) next.clientMatterNo = clientMatterNo
    if (pageSize !== undefined) next.filters.pageSize = pageSize
    if (orderByColumns !== undefined) next.filters.orderByColumns = orderByColumns
    if (startAtIndex !== undefined) next.filters.startAtIndex = startAtIndex
    if (totalRowCount !== undefined) next.totalRowCount = totalRowCount

    // Only set state if it actually changed
    const changed = JSON.stringify(next) !== JSON.stringify(current)
    if (changed) {
      if (isMatterSpecific) {
        set({ matterSpecificKeyDatesTableFilters: next })
      } else {
        set({ keyDatesTableFilters: next })
      }
    }

    // Recompute applied filter count
    const defaultFilters = isMatterSpecific
      ? initialState.matterSpecificKeyDatesTableFilters.filters
      : initialState.keyDatesTableFilters.filters

    const newFilterCount = countAppliedFilters(next.filters, defaultFilters, KEYDATES_IGNORED_FOR_COUNT)

    if (isMatterSpecific) {
      set({ matterSpecificKeyDatesTableFilterCount: newFilterCount })
    } else {
      set({ keyDatesTableFilterCount: newFilterCount })
    }
  },

  setMatterSpecificKeyDatesTableFilters: ({
    clientMatterNo,
    pageSize,
    orderByColumns,
    startAtIndex,
    totalRowCount,
    filterType,
    value,
    filters // allow bag merge for symmetry
  }) => {
    const { matterSpecificKeyDatesTableFilters: current } = get()

    let next = {
      ...current,
      filters: { ...current.filters }
    }

    if (filters && typeof filters === 'object') {
      next.filters = { ...next.filters, ...filters }
    }

    if (filterType != null) {
      const key = String(filterType).trim()
      if (key) next.filters[key] = value === undefined ? null : value
    }

    if (clientMatterNo !== undefined) next.clientMatterNo = clientMatterNo
    if (pageSize !== undefined) next.filters.pageSize = pageSize
    if (orderByColumns !== undefined) next.filters.orderByColumns = orderByColumns
    if (startAtIndex !== undefined) next.filters.startAtIndex = startAtIndex
    if (totalRowCount !== undefined) next.totalRowCount = totalRowCount

    const changed = JSON.stringify(next) !== JSON.stringify(current)
    if (changed) set({ matterSpecificKeyDatesTableFilters: next })

    const defaultFilters = initialState.matterSpecificKeyDatesTableFilters.filters
    const newCount = countAppliedFilters(next.filters, defaultFilters, KEYDATES_IGNORED_FOR_COUNT)
    set({ matterSpecificKeyDatesTableFilterCount: newCount })
  },

  fetchCurrentClientsTableFilters: () => {
    const { clientsTableFilters } = get()
    return clientsTableFilters
  },
  fetchCurrentMattersTableFilters: () => {
    const { mattersTableFilters } = get()
    return mattersTableFilters
  },
  fetchCurrentKeyDatesTableFilters: () => {
    const { keyDatesTableFilters } = get()
    return keyDatesTableFilters
  },
  fetchCurrentExpensesTableFilters: () => {
    const { expensesTableFilters } = get()
    return expensesTableFilters
  },
  fetchCurrentMatterSpecificKeyDatesTableFilters: () => {
    const { matterSpecificKeyDatesTableFilters } = get()
    return matterSpecificKeyDatesTableFilters
  },
  fetchOvertimeTableFilters: () => {
    const { overtimeTableFilters } = get()
    return overtimeTableFilters
  },
  fetchCurrentThirdPartyFormsTableFilters: () => {
    const { thirdPartyFormsTableFilters } = get()
    return thirdPartyFormsTableFilters
  },

  resetMatterTableFilters: () => {
    const { mattersTableFilters } = get()
    const defaultFilters = { ...initialState.mattersTableFilters.filters }

    set({
      mattersTableFilters: { ...mattersTableFilters, filters: defaultFilters },
      mattersTableFilterCount: 0
    })
  },
  resetClientTableFilters: () => {
    const { clientsTableFilters } = get()
    const defaultFilters = { ...initialState.clientsTableFilters.filters }

    set({
      clientsTableFilters: { ...clientsTableFilters, filters: defaultFilters },
      clientsTableFilterCount: 0
    })
  },
  resetKeyDatesTableFilters: () => {
    const { keyDatesTableFilters } = get()
    const defaultFilters = { ...initialState.keyDatesTableFilters.filters }

    set({
      keyDatesTableFilters: { ...keyDatesTableFilters, filters: defaultFilters },
      keyDatesTableFilterCount: 0
    })
  },
  resetMatterSpecificKeyDatesTableFilters: () => {
    const { matterSpecificKeyDatesTableFilters } = get()
    const defaultFilters = { ...initialState.matterSpecificKeyDatesTableFilters.filters }

    set({
      matterSpecificKeyDatesTableFilters: { ...matterSpecificKeyDatesTableFilters, filters: defaultFilters },
      matterSpecificKeyDatesTableFilterCount: 0
    })
  },
  resetExpensesTableFilters: () => {
    const { expensesTableFilters } = get()
    const defaultFilters = { ...initialState.expensesTableFilters.filters }

    set({
      expensesTableFilters: { ...expensesTableFilters, filters: defaultFilters },
      expensesTableFilterCount: 0
    })
  },
  resetOvertimeTableFilters: () => {
    const { overtimeTableFilters } = get()
    const defaultFilters = { ...initialState.overtimeTableFilters.filters }

    set({
      overtimeTableFilters: { ...overtimeTableFilters, filters: defaultFilters },
      overtimeTableFilterCount: 0
    })
  },
  resetThirdPartyFormsTableFilters: () => {
    const { thirdPartyFormsTableFilters } = get()
    const defaultFilters = { ...initialState.thirdPartyFormsTableFilters.filters }

    set({
      thirdPartyFormsTableFilters: { ...thirdPartyFormsTableFilters, filters: defaultFilters },
      thirdPartyFormsTableFilterCount: 0
    })
  },
  setDocumentBundlesTableFiltersFromPreferences: ({ filters }) => {
    if (!filters) return
    const { documentBundlesTableFilters } = get()
    const next = {
      ...documentBundlesTableFilters,
      filters: { ...documentBundlesTableFilters.filters, ...filters }
    }
    set({ documentBundlesTableFilters: next })
    const defaultFilters = initialState.documentBundlesTableFilters.filters
    const documentBundlesTableFilterCount = countAppliedFilters(next.filters, defaultFilters, KEYDATES_IGNORED_FOR_COUNT)
    set({ documentBundlesTableFilterCount })
  },
  setDocumentBundlesTableFilters: ({
    filterType,
    value,
    filters,
    pageSize,
    startAtIndex,
    totalRowCount
  }) => {
    const { documentBundlesTableFilters: current } = get()

    let next = {
      ...current,
      filters: { ...current.filters }
    }

    if (filters && typeof filters === 'object') {
      next.filters = { ...next.filters, ...filters }
    }

    if (filterType != null) {
      const key = String(filterType).trim()
      if (key) next.filters[key] = value === undefined ? null : value
    }

    if (pageSize !== undefined) next.filters.pageSize = pageSize
    if (startAtIndex !== undefined) next.filters.startAtIndex = startAtIndex
    if (totalRowCount !== undefined) next.totalRowCount = totalRowCount

    const changed = JSON.stringify(next) !== JSON.stringify(current)
    if (changed) set({ documentBundlesTableFilters: next })

    const defaultFilters = initialState.documentBundlesTableFilters.filters
    const newCount = countAppliedFilters(next.filters, defaultFilters, KEYDATES_IGNORED_FOR_COUNT)
    set({ documentBundlesTableFilterCount: newCount })
  },
  fetchCurrentDocumentBundlesTableFilters: () => {
    const { documentBundlesTableFilters } = get()
    return documentBundlesTableFilters
  },
  resetDocumentBundlesTableFilters: () => {
    const { documentBundlesTableFilters } = get()
    const defaultFilters = { ...initialState.documentBundlesTableFilters.filters }

    set({
      documentBundlesTableFilters: { ...documentBundlesTableFilters, filters: defaultFilters },
      documentBundlesTableFilterCount: 0
    })
  },
  reset: () => set(initialState)
}))

// Count how many filters differ from defaults.
// Optionally ignore specific keys (used for key-dates pagination/sort transport fields).
const countAppliedFilters = (filters, defaultFilters, ignoreKeys = new Set()) => {
  const allKeys = new Set([...Object.keys(defaultFilters || {}), ...Object.keys(filters || {})])

  const filterCount = Array.from(allKeys).reduce((acc, key) => {
    if (ignoreKeys.has(key)) return acc
    const item = filters[key]
    const sourceStr = JSON.stringify(item)
    const targetStr = JSON.stringify(defaultFilters[key])

    if (item !== null && item !== undefined && sourceStr !== targetStr) {
      return acc + 1
    }
    return acc
  }, 0)

  return filterCount
}
